import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatEmployeeTimeProjectNumber } from "@/lib/auftragAutocomplete";
import {
  formatDuration,
  formatHours,
  getActiveTimer,
  startEmployeeTimer,
  stopEmployeeTimer,
  type EmployeeTimeTimer,
} from "@/lib/employeeTimes";
import { MIXED_PROJECT_POSITION_REQUIRED } from "@/pages/admin/zeiterfassung/preisartChoice";

interface TimerWidgetProps {
  userLogin: string;
  auftragId: number | null;
  positionId?: number | null;
  /** Nachweis- und Festpreis-Projekt: Position ist vor dem Start Pflicht. */
  positionRequired?: boolean;
  comment: string;
  isHomeoffice?: boolean;
  onStopped: () => void;
  disabled?: boolean;
  /** Eingebettet in das Erfassungsformular ohne eigene Karte. */
  embedded?: boolean;
}

export function TimerWidget({
  userLogin,
  auftragId,
  positionId = null,
  positionRequired = false,
  comment,
  isHomeoffice = false,
  onStopped,
  disabled,
  embedded = false,
}: TimerWidgetProps) {
  const [timer, setTimer] = useState<EmployeeTimeTimer | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = await getActiveTimer(userLogin);
      setTimer(active);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Timer konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!timer) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [timer]);

  const elapsed = timer ? (timer.elapsed_seconds ?? 0) + tick : 0;

  const handleStart = async () => {
    if (!auftragId) {
      setError("Bitte zuerst ein Projekt wählen.");
      return;
    }
    if (positionRequired && positionId == null) {
      setError(MIXED_PROJECT_POSITION_REQUIRED);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const started = await startEmployeeTimer({
        auftrag_id: auftragId,
        user_login: userLogin,
        comment: comment || undefined,
        geschaeftsdokument_position_id: positionId,
        is_homeoffice: isHomeoffice,
      });
      setTimer(started);
      setTick(0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Timer konnte nicht gestartet werden.");
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    setBusy(true);
    setError(null);
    try {
      await stopEmployeeTimer(userLogin);
      setTimer(null);
      setTick(0);
      onStopped();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Timer konnte nicht gestoppt werden.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        !embedded && "rounded-ios border border-separator bg-card p-5 shadow-ios",
        embedded && "space-y-4",
      )}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <div
          className={clsx(
            "font-mono font-semibold tabular-nums text-accent",
            timer ? "text-5xl" : "text-4xl text-label-tertiary",
          )}
        >
          {timer ? formatDuration(elapsed) : "0:00"}
        </div>
        {timer && (
          <p className="text-sm text-label-secondary">≈ {formatHours(elapsed / 3600)}</p>
        )}
        {!timer && (
          <p className="text-sm text-label-secondary">
            Projekt wählen, dann Timer starten.
          </p>
        )}
      </div>

        {timer && (
          <div className="rounded-lg bg-fill-tertiary px-3 py-2 text-center text-sm text-label-secondary">
            <span className="font-medium text-label">
              {formatEmployeeTimeProjectNumber(timer) || timer.auftragsnummer}
            </span>
            {[timer.beschreibung, timer.kunde, timer.ort_zusatz || timer.ort]
              .filter(Boolean)
              .map((part, idx) => (
                <span key={`${idx}-${part}`}> · {part}</span>
              ))}
            {timer.is_homeoffice ? (
              <div className="mt-1 text-xs font-medium text-accent">Homeoffice</div>
            ) : null}
          </div>
        )}

      {error && <Alert variant="error" title="Timer">{error}</Alert>}

      <div className="flex justify-center">
        {!timer ? (
          <Button
            variant="primary"
            size="md"
            className="min-w-[200px]"
            onClick={() => void handleStart()}
            disabled={disabled || busy || !auftragId}
          >
            Timer starten
          </Button>
        ) : (
          <Button
            variant="danger"
            size="md"
            className="min-w-[200px]"
            onClick={() => void handleStop()}
            disabled={busy}
          >
            Stoppen &amp; buchen
          </Button>
        )}
      </div>
    </div>
  );
}
